import React, { useEffect, useRef } from "react";

export default function BackgroundAnimation() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const particles = [];
    const particleCount = Math.min(110, Math.floor((width * height) / 15000)); // Higher density for stronger graphics
    const connectionDistance = 140; // Larger connection distance
    const mouse = { x: null, y: null, radius: 180 };

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.5; // Slightly faster motion
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 3 + 1.5; // Larger particles (1.5px to 4.5px)
        this.alpha = Math.random() * 0.5 + 0.3; // Higher base opacity
        // Neon Cyan and Vibrant Purple
        this.colorType = Math.random() > 0.5 ? "cyan" : "purple";
        this.color = this.colorType === "cyan" 
          ? `rgba(0, 242, 254, ${this.alpha})` 
          : `rgba(167, 139, 250, ${this.alpha})`;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Bounce/Wrap borders
        if (this.x < 0 || this.x > width) this.vx = -this.vx;
        if (this.y < 0 || this.y > height) this.vy = -this.vy;

        // Mouse interaction (stronger magnetic push)
        if (mouse.x !== null && mouse.y !== null) {
          const dx = this.x - mouse.x;
          const dy = this.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < mouse.radius) {
            const force = (mouse.radius - dist) / mouse.radius;
            // Particles drift away smoothly
            this.x += (dx / dist) * force * 1.5;
            this.y += (dy / dist) * force * 1.5;
          }
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        
        // Add subtle radial glow around particles
        ctx.fillStyle = this.color;
        ctx.shadowBlur = this.radius * 2;
        ctx.shadowColor = this.colorType === "cyan" ? "#00f2fe" : "#a78bfa";
        ctx.fill();
        ctx.shadowBlur = 0; // Reset shadow for lines
      }
    }

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    // Rotation counter for cosmic nebula motion
    let angle = 0;

    // Animation Loop
    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      // ── DYNAMIC VIBRANT COSMIC NEBULA GLOWS ──
      angle += 0.001;
      const glowX1 = width * 0.2 + Math.cos(angle) * 100;
      const glowY1 = height * 0.2 + Math.sin(angle) * 100;
      const glowX2 = width * 0.8 - Math.cos(angle) * 150;
      const glowY2 = height * 0.8 - Math.sin(angle) * 150;

      // Cyan Nebula (Top Left region)
      const gradient1 = ctx.createRadialGradient(glowX1, glowY1, 0, glowX1, glowY1, width * 0.5);
      gradient1.addColorStop(0, "rgba(0, 242, 254, 0.06)"); // Higher opacity for more visibility
      gradient1.addColorStop(0.5, "rgba(0, 242, 254, 0.02)");
      gradient1.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = gradient1;
      ctx.fillRect(0, 0, width, height);

      // Purple/Lavender Nebula (Bottom Right region)
      const gradient2 = ctx.createRadialGradient(glowX2, glowY2, 0, glowX2, glowY2, width * 0.6);
      gradient2.addColorStop(0, "rgba(167, 139, 250, 0.05)");
      gradient2.addColorStop(0.5, "rgba(139, 92, 246, 0.01)");
      gradient2.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = gradient2;
      ctx.fillRect(0, 0, width, height);

      // Interactive mouse aura glow
      if (mouse.x !== null && mouse.y !== null) {
        const mouseGlow = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, mouse.radius * 0.6);
        mouseGlow.addColorStop(0, "rgba(0, 242, 254, 0.04)");
        mouseGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = mouseGlow;
        ctx.fillRect(0, 0, width, height);
      }

      // Render connected lines and updates
      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i];
        p1.update();
        p1.draw();

        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < connectionDistance) {
            const alpha = (1 - dist / connectionDistance) * 0.28; // Significantly stronger line opacity
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            
            // Neon gradient connection color
            ctx.strokeStyle = p1.colorType === "cyan" 
              ? `rgba(0, 242, 254, ${alpha})` 
              : `rgba(167, 139, 250, ${alpha})`;
              
            ctx.lineWidth = 0.7; // Slightly thicker lines for visibility
            ctx.stroke();
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Event listeners
    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    const handleMouseLeave = () => {
      mouse.x = null;
      mouse.y = null;
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 0,
        background: "transparent", 
      }}
    />
  );
}
